import { Component, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, Input, OnInit } from '@angular/core';

import { Util } from '../utils/utils.module';
import { ListItem } from '../models/list-item';
import { AccessLevelExternal } from './../models/security-control-external';
import { LocalizeService } from '../services/localize.service';
import { ListService } from '../services/list.service';
import { MenuId } from '../services/menu.service';
import { FormService } from '../services/form.service';
import { ListTableComponent } from './list-table.component';
import { ListSecurityComponent } from './list-security.component';

@Component({
  selector: 'list-security-external',
  styleUrls: ['list-security.component.scss'],
  providers: [ListService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: 'list-security-external.component.html',
  standalone: false
})
export class ListSecurityExternalComponent extends ListSecurityComponent implements OnInit {
  private foundUsers: any[];
  private counter = 0;  /* keeps track of times security list gets edited */
  public extNewFolder = false;
  public originalExtSecurityList: ListItem[];
  @ViewChild('userSearch') userSearch: ElementRef<HTMLElement> | undefined;
  @ViewChild('userComment') userComment: ElementRef<HTMLElement> | undefined;
  @Input('popup') popup: any;

  constructor(protected localizer: LocalizeService, protected listService: ListService, protected formService: FormService, protected cdr: ChangeDetectorRef) {
    super(localizer, listService, formService, cdr);
    this.actionMenuId = MenuId.MENU_EXT_SECURITY_ACTIONS;
  }

  ngOnInit(): void {
    this.extNewFolder = this.kind === 'corefolders';
  }

  protected onTemplateRightChange(item: any): void {
    if (this.counter === 0) {
      this.originalExtSecurityList = Util.deepCopy(this.securityList);
      const editedListItem = item[0];
      const oldRight = item[1];

      const foundUserIndex = this.originalExtSecurityList.findIndex(item =>
        item['USER_ID'] === editedListItem['USER_ID'] && item['FULL_NAME'] === editedListItem['FULL_NAME']);

      if (foundUserIndex !== -1) {
        this.originalExtSecurityList[foundUserIndex]['rights'] = oldRight;
      }
    }
    this.counter++;

    this.isDirty = true;
    this.notifySecurityDirty.emit(this.isDirty);
  }

  protected checkUserRights(): void {
    const trustees: ListItem[] = this.getList();
    const coreUser = this.desc?.['coreUser']?.split('@')[0] || Util.RestAPI.getUserID();

    for (const trustee of trustees) {
      const userID = trustee['USER_ID'].split('@')[0];
      const rights = trustee['rights'];
      const canEdit = rights === AccessLevelExternal.ACCESS_LEVEL_MANAGER; // Owner has manager right

      if (Util.Transforms.isCaseInsensitiveEqual(userID, coreUser) && canEdit) {
        this.notifyReadonly(!canEdit);
        this.popup.okDisabled = false;
        break;
      } else {
        this.notifyReadonly(true);
        this.popup.okDisabled = true;
      }
    }
    this.cdr.markForCheck();
  }

  public canSelectListItem(table: ListTableComponent, item: ListItem): boolean {
    return !item['owner'] && !item['inherited'];
  }

  // **** CommandHandler implementation
  public commandEnabled(cmd: string): boolean {
    let enabled = false;
    if (!!this.desc) {
      switch (cmd) {
        case 'security_remove':
          enabled = false;
          if (this.selectedListItems && (this.selectedListItems.length > 0)) {
            const authorID = this.desc['AUTHOR_ID']?.split('@')[0];
            const rights = this.desc['permission'];
            const canEdit = rights === AccessLevelExternal.ACCESS_LEVEL_MANAGER; // Owner has manager right
            enabled = this.securityList.some(item => {
              const userID = item['USER_ID']?.split('@')[0];
              if (Util.Transforms.isCaseInsensitiveEqual(userID, authorID) && canEdit) {
                return true;
              }
              return false;
            });
          }
          break;
      }
    }
    return enabled;
  }

  public doCommand(cmd: string): boolean {
    const handled = true;

    switch (cmd) {
      case 'security_remove': {
        const selections: ListItem[] = this.table.getSelections();
        const list: ListItem[] = this.table.getList();
        if (list && list.length && selections && selections.length) {
          if (this.counter === 0) {
            this.originalExtSecurityList = Util.deepCopy(list);
          }
          const indexesToDelete: number[] = [];
          for (const listItem of selections) {
            indexesToDelete.push(list.indexOf(listItem));
          }
          indexesToDelete.sort((a, b) => b - a);
          for (const index of indexesToDelete) {
            list.splice(index, 1);
          }
          this.securityList = list;
          this.counter++;
          this.isDirty = true;
          this.notifySecurityDirty.emit(this.isDirty);
          this.table.clearSelection();
        }
        break;
      }
    }
    this.selectedListItems = [];
    if (this.actionBar) {
      this.actionBar.updateActions();
    }
    return handled;
  }

  onKeyupSearch(e) {
    let options = [];
    const lib = this.desc.lib;
    const target = e.target as HTMLInputElement;
    const name = target['selectInputValue']?.toLowerCase();
    const url = `/lookups/contacts/search?library=${lib}&key=${name}`;
    let isValidEmail = false;

    if (name) {
      if (e.code !== 'ArrowDown' && e.code !== 'Space') { //user selecting from the available list
        this.foundUsers = [];

        Util.RestAPI.get(url).subscribe((data) => {
          if (data.results?.length) {
            const users = data['results'];
            options = users.map((user) => {
              this.foundUsers.push({ 'Name': user.displayName, 'Email': user.email ? user.email : '' });

              const initial = user.firstName
                ? `${user.firstName?.slice(0, 1)}${user.lastName?.slice(0, 1)}`
                : `${user.displayName?.slice(0, 2).toUpperCase()}`;

              return `<otc-selectuseritem initials="${initial}" value="${user.displayName}" displayvalue="${user.displayName}">
                        ${user.displayName}
                        <otc-usercard>
                          ${user.email ? user.email : ''}
                        </otc-usercard>
                      </otc-selectuseritem>`;
            });
          } else {
            isValidEmail = name.match(/\S+@\S+\.\S+/);
          }

          setTimeout(() => {
            const otcSelect = document.querySelector('#search-collaborator');

            otcSelect.innerHTML = options.join('');
            otcSelect['displayValue'] = target['selectInputValue'];

            if (isValidEmail && !this.isReadOnly) {
              document.querySelector('#add-collaborator').removeAttribute('disabled');
            } else {
              document.querySelector('#add-collaborator').setAttribute('disabled', 'true');
            }
          }, 100);
        });
      }
    } else if (name === '') {
      setTimeout(() => {
        target['displayValue'] = '';
        target.innerHTML = '';
        document.querySelector('#add-collaborator').setAttribute('disabled', 'true');
      }, 100);
    }
  }

  onChangeSearch(event) {
    const target = event.target as HTMLInputElement;
    const selectedValue = target['selectInputValue'];
    const userAdded = this.securityList?.find(user => user['FULL_NAME'] === selectedValue);

    if (selectedValue && !userAdded && !this.isReadOnly) {
      setTimeout(() => {
        document.querySelector('#add-collaborator').removeAttribute('disabled');
      }, 100);
    } else {
      setTimeout(() => {
        document.querySelector('#add-collaborator').setAttribute('disabled', 'true');
      }, 100);
    }
  }

  addUser() {
    let selectedUser: any[];
    const selectedValue = this.userSearch.nativeElement?.['selectInputValue'];
    const commentValue = this.userComment.nativeElement?.['value'];

    if (selectedValue) {
      if (!this.foundUsers.length) {
        this.foundUsers.push({ 'Name': '', 'Email': selectedValue });
      }
      const foundIndex = this.foundUsers.findIndex(user => (user.Email === selectedValue || user.Name === selectedValue));
      if (foundIndex !== -1) {
        selectedUser = [{
          flag: 2,
          USER_ID: this.foundUsers[foundIndex]['Email'],
          FULL_NAME: this.foundUsers[foundIndex]['Name'],
          rights: AccessLevelExternal.ACCESS_LEVEL_VIEWER,
          message: commentValue
        }];
      }

      if (this.counter === 0) { /* Get a copy of the list if it hasn't changed yet */
        this.originalExtSecurityList = Util.deepCopy(this.securityList);
      }

      const newTrusteeOrGroupAdded: boolean = this.table.addTrusteeToList(selectedUser);
      this.securityList = this.table.getList();
      if (newTrusteeOrGroupAdded) {
        this.counter++;
        setTimeout(() => {
          document.querySelector('#add-collaborator').setAttribute('disabled', 'true');
          document.querySelector('#comment-collaborator')['value'] = '';
          const otcSelect = document.querySelector('#search-collaborator');
          otcSelect.innerHTML = '';
          otcSelect['value'] = '';
          otcSelect['displayValue'] = '';
        }, 100);
        this.isDirty = true;
        this.notifySecurityDirty.emit(this.isDirty);
        this.checkUserRights();
        this.actionBar.updateActions();
      }
    }
    this.table.getList();
  }

  onValueChange(event) {
    const target = event.target as HTMLInputElement;
    const folderName = (target?.value || '').trim();
    document.querySelector('#folder-name').setAttribute('value', folderName);

    if (folderName) {
      setTimeout(() => {
        this.popup.okDisabled = false;
      }, 10);
    } else {
      setTimeout(() => {
        this.popup.okDisabled = true;
      }, 10);
    }
  }
}
